import { useDebouncedCallback } from '@react-hookz/web';
import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import type { JavaScriptFunctionState } from '@unifyapps/defs/types/javascriptFunction';
import _get from 'lodash/get';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { isCompleteDependency } from '../../../dependency/const';
import { ComputeContextKeys } from '../../../hooks/computeContext/const';
import { useGetShouldEvaluateResult } from '../../../hooks/computeContext/useGetShouldEvaluateResult';
import {
  usePFCDependencyGraphProvider,
  usePFCGlobalStateStore,
  usePFCInterfaceStore,
  usePFCNonReactiveComputeContext,
  usePFCUseIsEntitySuspended,
  usePFCUseTriggerAutomaticDataSources,
} from '../context/selectors';
import { type ExecuteFunctionResult, executeFunction } from './executeFunction';

type EvaluatePageFunctionProps = {
  context: Record<string, unknown>;
  depSlice: Record<string, unknown>;
  functionBody: string;
  functionId: string;
  shouldEvaluateResult: (result: string) => boolean;
  onAfterEvaluate: (result: ExecuteFunctionResult) => void;
};

function PageFunctionExecutor({ id, interfacePageId }: { id: string; interfacePageId?: string }) {
  const pageFunction = usePFCInterfaceStore().hooks.page.usePageFunctionById(id, interfacePageId);
  const getGlobalStateStoreState = usePFCGlobalStateStore().getState;
  const { context } = usePFCNonReactiveComputeContext({
    entityId: id,
  });

  const { dataSourceIds, functionBody = '' } = pageFunction ?? {};
  // Written at schedule time, not after eval — debounce swallows duplicates.
  const lastDepSliceRef = useRef<Record<string, unknown> | undefined>(undefined);
  // Body isn't in graph deps; gate it separately so edits re-run.
  const lastFunctionBodyRef = useRef<string | undefined>(undefined);

  const {
    actions: { notifyDependencyChange },
    graphManager,
  } = usePFCDependencyGraphProvider();

  // Compare only this function's declared deps, not the full context.
  // usePFCNonReactiveComputeContext gates re-render timing, not its returned
  // value — `context` is rebuilt full every render (computeContext/index.ts),
  // so save-flow churn (`updatedAt` bumps, server replay) defeats a
  // full-context isEqual and re-runs the body twice per save.
  //
  // Reactivity contract: function reacts only to values the dep parser picks
  // up (standard binding syntax + known accessors). Dynamic access
  // (`globalThis.foo`, computed keys) won't re-trigger. Body still receives
  // the full context at eval — slice only gates the decision.
  const depSlice = useMemo<Record<string, unknown>>(() => {
    const dependsOn = graphManager.getNode(id)?.dependsOn;
    if (!dependsOn?.length) return EMPTY_OBJECT;
    const slice: Record<string, unknown> = {};
    for (const dep of dependsOn) {
      if (isCompleteDependency(dep.id, dep.p)) {
        slice[dep.id] = context[dep.id];
      } else {
        for (const path of dep.p) {
          slice[path] = _get(context, path);
        }
      }
    }
    return slice;
  }, [context, graphManager, id]);

  const shouldEvaluateResult = useGetShouldEvaluateResult();

  usePFCUseTriggerAutomaticDataSources({
    dataSourceIds: dataSourceIds,
  });

  // In page function we wait for the data sources this page function is dependent on to be resolved before we evaluate the page function
  const { isSuspended } = usePFCUseIsEntitySuspended({
    dataSourceIds: dataSourceIds,
    context,
  });

  // debounced function to evaluate the page function
  // defined inside component such that each pageFunction gets its own debounced function
  // else using a single debounced/throttled function would cause issues related to some pageFunctions not getting evaluated
  const debouncedEvaluatePageFunction = useDebouncedCallback(
    function evaluatePageFunction({
      context,
      depSlice: currentDepSlice,
      functionBody,
      functionId,
      shouldEvaluateResult,
      onAfterEvaluate,
    }: EvaluatePageFunctionProps) {
      const result = executeFunction({ context, functionBody, shouldEvaluateResult, functionId });
      lastDepSliceRef.current = currentDepSlice;
      lastFunctionBodyRef.current = functionBody;
      onAfterEvaluate(result);
    },
    [],
    300,
  );

  const onAfterEvaluate = useCallback(
    (result: ExecuteFunctionResult) => {
      const globalStateStoreState = getGlobalStateStoreState();
      const latestPageFunctionResult = globalStateStoreState.pageFunctions[id];

      if (!latestPageFunctionResult) {
        console.error('Page function state not found after evaluation', id);
        return;
      }

      const hasValueChange = !isEqual(result.value, latestPageFunctionResult.value);
      const hasErrorChange = !isEqual(result.error, latestPageFunctionResult.error);

      if (!hasValueChange && !hasErrorChange && latestPageFunctionResult.isEvaluated) {
        return;
      }

      const newState: JavaScriptFunctionState = {
        ...latestPageFunctionResult,
        isEvaluated: true,
        value: result.error ? undefined : result.value,
        error: result.error,
      };

      globalStateStoreState.actions.setPageFunctionState(latestPageFunctionResult.id, newState);
      notifyDependencyChange({ id: latestPageFunctionResult.id });
      notifyDependencyChange({ id: ComputeContextKeys.PAGE_FUNCTIONS });
    },
    [getGlobalStateStoreState, id, notifyDependencyChange],
  );

  useEffect(() => {
    if (isSuspended) return;
    const depSliceChanged = !isEqual(depSlice, lastDepSliceRef.current);
    const functionBodyChanged = functionBody !== lastFunctionBodyRef.current;
    if (depSliceChanged || functionBodyChanged) {
      debouncedEvaluatePageFunction({
        context,
        depSlice,
        functionBody,
        functionId: id,
        onAfterEvaluate,
        shouldEvaluateResult,
      });
    }
  }, [
    depSlice,
    context,
    debouncedEvaluatePageFunction,
    onAfterEvaluate,
    shouldEvaluateResult,
    functionBody,
    isSuspended,
    id,
  ]);

  return null;
}

export default memo(PageFunctionExecutor);
